import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, fmtMoney } from "../api";
import type { ApiError, PaymentMethod } from "../api";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { MoneyInput } from "./money-input";
import { PaymentMethodSelect } from "./payment-method-select";

/**
 * Mijozdan olingan pulni qarz daftariga yozish.
 *
 * To'lov BALANSGA yoziladi, hujjatga emas — daftar allaqachon yuruvchi balans,
 * ya'ni egadan bitta to'lovni uch xil eski ish orasida taqsimlashni so'rash u
 * so'ramagan buxgalteriya bo'lardi.
 *
 * Ikkita nishon: kartochkali mijoz (`client`) yoki kartochkasiz nomli qarzdor
 * (`debtor`) — ikkalasi ham bir xil endpoint'ga boradi.
 *
 * Ortiqcha to'lov RUXSAT ETILGAN (mijozlar avans qoldiradi), lekin JIMGINA
 * emas: server tasdiqsiz rad etadi, shuning uchun bu yerda ikki bosqichli
 * tasdiq bor — birinchi bosqich ogohlantiradi va tugma matnini "avans sifatida
 * yozish" ga o'zgartiradi.
 */
export type DebtPaymentTarget =
  | { kind: "client"; clientId: number; name: string }
  | { kind: "debtor"; name: string };

export function DebtPaymentDialog({
  target,
  balans,
  onClose,
  onSaved,
}: {
  target: DebtPaymentTarget;
  /** Joriy balans: musbat = qarzdor, manfiy = avans. */
  balans: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["qarzlar", "common"]);
  const joriy = Number(balans) || 0;
  // To'lash mumkin bo'lgan ochiq qarz. Manfiy balans (avans) NOLGA tenglashadi
  // — serverdagi `max(balans, 0)` qoidasi bilan AYNAN bir xil, aks holda
  // ekrandagi "ortiqcha" raqami server hisoblaganidan farq qilardi.
  const ochiq = Math.max(0, joriy);
  const [summa, setSumma] = useState(ochiq ? String(Math.round(ochiq)) : "");
  const [usul, setUsul] = useState<PaymentMethod | "">("");
  const [izoh, setIzoh] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Avans tasdiqlanganmi. Ikki yo'l bilan yoqiladi: foydalanuvchi ortiqcha
  // summa kiritganda (birinchi "Saqlash" bosilishida) yoki server 400 bilan
  // rad etganda (balans forma ochilgandan keyin o'zgargan bo'lsa).
  const [avans, setAvans] = useState(false);

  const n = Number(summa) || 0;
  const ortiqcha = n - ochiq;
  const qoladi = joriy - n;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (n <= 0) {
      setErr(t("payment.amountRequired"));
      return;
    }
    // Birinchi bosqich: ortiqcha summa kiritilgan, lekin hali tasdiqlanmagan.
    if (ortiqcha > 0 && !avans) {
      setAvans(true);
      setErr(null);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.debtTolov({
        ...(target.kind === "client"
          ? { client_id: target.clientId }
          : { debtor_name: target.name }),
        summa: String(n),
        tolov_usuli: usul || null,
        izoh: izoh.trim() || null,
        avans_tasdiqlandi: avans,
      });
      toast.success(t("payment.success"));
      onSaved();
    } catch (e) {
      const ae = e as ApiError;
      // Balans forma ochilgandan keyin o'zgargan bo'lishi mumkin — serverning
      // haqiqiy raqamini ko'rsatib, tasdiq bosqichiga o'tamiz.
      if (ae.code === "payment_exceeds_balance") {
        setAvans(true);
        setErr(
          t("payment.advanceWarn", {
            summa: fmtMoney(String(ae.details?.ortiqcha ?? ortiqcha)),
          }),
        );
      } else {
        setErr(ae.message || t("common:common.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} contentClassName="max-w-sm">
      <form onSubmit={submit} className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("payment.title", { name: target.name })}
          </h2>
        </div>

        {/* Kirish paytida ko'rinadigan raqamlar: qancha qarz va to'lovdan keyin
            qancha qoladi. Ko'r-ko'rona kiritish — noto'g'ri raqam yozilishining
            asosiy sababi. */}
        <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("payment.currentDebtLabel")}
            </span>
            <span
              className={`text-lg font-bold tabular-nums ${joriy > 0 ? "text-destructive" : joriy < 0 ? "text-success" : ""}`}
            >
              {fmtMoney(joriy)}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("payment.qoladi")}
            </span>
            <span className="font-medium tabular-nums">
              {fmtMoney(qoladi)}
              {qoladi < 0 && (
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  {t("payment.advanceTag")}
                </span>
              )}
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="debt-tolov-summa">{t("payment.summa")}</Label>
          <MoneyInput
            id="debt-tolov-summa"
            value={summa}
            onChange={(v) => {
              setSumma(v);
              // Summa o'zgarsa tasdiq bekor bo'ladi — ega yangi raqamni
              // ko'rmasdan avansni tasdiqlab yubormasin.
              setAvans(false);
              setErr(null);
            }}
            disabled={busy}
            autoFocus
            className="text-right tabular-nums"
            placeholder="0"
          />
        </div>

        <PaymentMethodSelect
          id="debt-tolov-usul"
          value={usul}
          onChange={setUsul}
          disabled={busy}
          className="space-y-1.5"
        />

        <Input
          value={izoh}
          onChange={(e) => setIzoh(e.target.value)}
          disabled={busy}
          placeholder={t("payment.izoh")}
        />

        {ortiqcha > 0 && (
          <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            {t("payment.advanceWarn", { summa: fmtMoney(ortiqcha) })}
          </div>
        )}

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="flex-none"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" disabled={busy || n <= 0} className="flex-1">
            {busy
              ? t("common:actions.processing")
              : ortiqcha > 0
                ? t("payment.confirmAdvance")
                : t("form.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
